import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { X } from "@phosphor-icons/react";

export function CreateTaskModal({ projectId, members, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    worker_id: "",
    checker_id: "",
    video_editor_id: "",
    due_date: "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const workers = members.filter((m) => m.role === "worker" || m.role === "assigner");
  const checkers = members.filter((m) => m.role === "checker" || m.role === "assigner" || m.role === "admin");
  const videoEditors = members.filter((m) => m.role === "video_editor" || m.role === "admin");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.worker_id) {
      setError("Please choose a Producer for this task.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await api.post(`/projects/${projectId}/tasks`, {
        title: form.title,
        description: form.description,
        worker_id: form.worker_id,
        checker_id: form.checker_id || null,
        video_editor_id: form.video_editor_id || null,
        due_date: form.due_date || null,
      });
      onCreated();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setSaving(false);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 1040 }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-[color:var(--border)]">
          <div>
            <p className="label-mono">New directive</p>
            <h2 className="font-heading text-xl font-bold tracking-tight mt-1">Create task</h2>
          </div>
          <button onClick={onClose} data-testid="close-create-task" className="text-[color:var(--text-secondary)] hover:text-white"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="label-mono block mb-2">Title</label>
            <input data-testid="task-title-input" required className="input" value={form.title} onChange={set("title")} placeholder="Write final copy for landing page" />
          </div>
          <div>
            <label className="label-mono block mb-2">Script</label>
            <textarea
              data-testid="task-description-input"
              className="input font-mono text-sm"
              value={form.description}
              onChange={set("description")}
              placeholder="Paste or write the script here — this is the source of truth for the Producer."
              rows={12}
              style={{ minHeight: 280, resize: "vertical" }}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label-mono block mb-2">Producer <span className="text-red-400">*</span></label>
              <select data-testid="task-worker-select" required className="select-tactical" value={form.worker_id} onChange={set("worker_id")}>
                <option value="" disabled>— Choose a Producer —</option>
                {workers.map((m) => (
                  <option key={m.id} value={m.id}>{m.name} ({m.role === "assigner" ? "assigner · producer" : "producer"})</option>
                ))}
              </select>
              {workers.length === 0 && (
                <p className="text-xs text-[color:var(--text-tertiary)] mt-2">
                  No producers in this project yet — invite a producer or assigner from the Team tab first.
                </p>
              )}
            </div>
            <div>
              <label className="label-mono block mb-2">Video Editor</label>
              <select data-testid="task-video-editor-select" className="select-tactical" value={form.video_editor_id} onChange={set("video_editor_id")}>
                <option value="">— None —</option>
                {videoEditors.map((m) => (
                  <option key={m.id} value={m.id}>{m.name} ({m.role === "video_editor" ? "video editor" : m.role})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-mono block mb-2">Copy Editor</label>
              <select data-testid="task-checker-select" className="select-tactical" value={form.checker_id} onChange={set("checker_id")}>
                <option value="">— None —</option>
                {checkers.map((m) => (
                  <option key={m.id} value={m.id}>{m.name} ({m.role === "checker" ? "copy editor" : m.role})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-mono block mb-2">Due date</label>
              <input data-testid="task-due-date-input" type="date" className="input" value={form.due_date} onChange={set("due_date")} />
            </div>
          </div>
          {error && <div className="text-sm text-red-400">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1 justify-center">Cancel</button>
            <button data-testid="submit-create-task-button" disabled={saving} className="btn-primary flex-1 justify-center">
              {saving ? "Creating..." : "Deploy task"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
